import { useState } from "react";
import { Package, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/Button";
import api from "../../lib/api";

const zipContents = [
  "journalen.csv — alle boekingen (verkoop, inkoop, bank) met debet/credit/btw",
  "relaties/ — overzicht per relatie (facturen, betalingen, uitgaven)",
  "btw-overzicht.csv — btw per tarief",
  "overzichten/ — winst & verlies, balans en boekjaarrapport als PDF",
  "facturen/ — alle facturen van de periode als PDF",
  "bonnetjes/ — ontvangstbewijzen en bankimport-bronbestanden",
  "manifest.json — tellingen en controle-informatie",
];

/** Accountant-pakket: download een ZIP met alle documenten en overzichten. */
export function AccountantPackagePanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState<number | "">("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/api/accountant-package", { year, ...(month !== "" ? { month } : {}) }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accountantpakket-${month !== "" ? `${year}-${String(month).padStart(2, "0")}` : year}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert("Accountant-pakket genereren mislukt. Probeer het opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="surface-card p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center">
            <Package size={26} />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Accountant-pakket</h3>
            <p className="text-sm text-slate-500">Eén ZIP met alles wat je accountant nodig heeft voor de gekozen periode.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Jaar</label>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setYear((y) => y - 1)}><ChevronLeft size={16} /></Button>
              <span className="font-semibold text-slate-700 w-12 text-center">{year}</span>
              <Button variant="ghost" size="sm" onClick={() => setYear((y) => y + 1)}><ChevronRight size={16} /></Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Periode</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value === "" ? "" : Number(e.target.value))}
              className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              <option value="">Hele jaar</option>
              {["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"].map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <Button onClick={download} loading={busy}>
            <Download size={18} /> Pakket downloaden (ZIP)
          </Button>
        </div>
      </div>

      <div className="surface-card p-6">
        <h3 className="font-bold text-slate-800 mb-3">Inhoud van het pakket</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          {zipContents.map((c, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-brand-500 mt-0.5">•</span>
              {c}
            </li>
          ))}
        </ul>
        <p className="text-xs text-slate-400 mt-4">Grotere periodes (heel jaar met veel documenten) kunnen even duren — de download start zodra het pakket klaar is.</p>
      </div>
    </div>
  );
}